// 36px header: product mark · LIVE pill · live tape strip ·
// classification banner · user menu.
//
// Classification banner is a 4px solid stripe at the top of the viewport
// colored by the active tenant's classification level. Reads from the
// authenticated `me` profile.

import { LocaleToggle } from '@/components/LocaleToggle';
import { Tooltip } from '@/components/Tooltip';
import { apiFetch, authFetch } from '@/lib/api';
import { timeShort } from '@/lib/format';
import { Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Each tape item carries metadata for the hover card. `live: false` = no feed
// yet. T13: values stay '—' until a real feed lands — no fabricated numbers.
interface TapeItem {
  k: string;
  v: string;
  /** Full display name shown in the hover card header. */
  label: string;
  /** Sub-label under the big number (e.g. "events · 24h"). */
  metricLabel?: string;
  /** ISO timestamp of last successful ingest — shown in card footer. */
  asOf?: string;
  /** integer delta chip (▲/▼). */
  d?: number;
  descKey: string;
  source: string;
  sourceUrl?: string;
  live: boolean;
}

const NO_FEED = '—';

const TAPE: TapeItem[] = [
  {
    k: 'OPP',
    label: 'Active Solicitations',
    metricLabel: 'open now',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.opp',
    source: 'Eidollon — all ingested portals',
    live: false,
  },
  {
    k: 'SAN',
    label: 'OFAC Sanctions Monitor',
    metricLabel: 'SDN entries',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.ofac',
    source: 'US Treasury OFAC SDN List',
    sourceUrl: 'https://sanctionssearch.ofac.treas.gov',
    live: false,
  },
  {
    k: 'CFL',
    label: 'ACLED Conflict Monitor',
    metricLabel: 'conflict events',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.conflict',
    source: 'Armed Conflict Location & Event Data (ACLED)',
    sourceUrl: 'https://acleddata.com',
    live: false,
  },
  {
    k: 'SEI',
    label: 'USGS Earthquake Feed',
    metricLabel: 'seismic events',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.seismic',
    source: 'USGS Earthquake Hazards Program',
    sourceUrl: 'https://earthquake.usgs.gov/earthquakes/feed/',
    live: false,
  },
  {
    k: 'NEWS',
    label: 'GDELT News Signal',
    metricLabel: 'news signals',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.news',
    source: 'GDELT Project',
    sourceUrl: 'https://www.gdeltproject.org',
    live: false,
  },
  {
    k: 'FIR',
    label: 'NASA FIRMS Active Fire',
    metricLabel: 'fire detections',
    v: NO_FEED,
    descKey: 'shell.header.tapeDesc.fire',
    source: 'NASA FIRMS (Fire Information for Resource Management)',
    sourceUrl: 'https://firms.modaps.eosdis.nasa.gov',
    live: false,
  },
];

const CLASS_TONE: Record<string, { bg: string; label: string }> = {
  PUBLIC: { bg: 'var(--fg-3)', label: 'PUBLIC' },
  CUI: { bg: 'var(--amber)', label: 'CUI' },
  SECRET: { bg: 'var(--green)', label: 'SECRET' },
  TS: { bg: 'var(--red)', label: 'TS' },
  TS_SCI: { bg: 'var(--red)', label: 'TS//SCI' },
};

interface FeedStat {
  count_24h?: number;
  total?: number;
  ingested_at: string | null;
}

interface TapeStats {
  ofac: (FeedStat & { total: number }) | null;
  acled: (FeedStat & { count_24h: number }) | null;
  usgs: (FeedStat & { count_24h: number }) | null;
  gdelt: (FeedStat & { count_24h: number }) | null;
  firms: (FeedStat & { count_24h: number }) | null;
  opp: { active: number } | null;
}

function applyTapeStats(base: TapeItem[], stats: TapeStats | null): TapeItem[] {
  if (!stats) return base;
  return base.map((item) => {
    if (item.k === 'OPP' && stats.opp)
      return { ...item, v: stats.opp.active.toLocaleString('en-US'), live: true };
    if (item.k === 'SAN' && stats.ofac)
      return {
        ...item,
        v: stats.ofac.total.toLocaleString('en-US'),
        asOf: stats.ofac.ingested_at ?? undefined,
        live: true,
      };
    if (item.k === 'CFL' && stats.acled)
      return {
        ...item,
        v: stats.acled.count_24h.toLocaleString('en-US'),
        asOf: stats.acled.ingested_at ?? undefined,
        live: true,
      };
    if (item.k === 'SEI' && stats.usgs)
      return {
        ...item,
        v: stats.usgs.count_24h.toLocaleString('en-US'),
        asOf: stats.usgs.ingested_at ?? undefined,
        live: true,
      };
    if (item.k === 'NEWS' && stats.gdelt)
      return {
        ...item,
        v: stats.gdelt.count_24h.toLocaleString('en-US'),
        asOf: stats.gdelt.ingested_at ?? undefined,
        live: true,
      };
    if (item.k === 'FIR' && stats.firms)
      return {
        ...item,
        v: stats.firms.count_24h.toLocaleString('en-US'),
        asOf: stats.firms.ingested_at ?? undefined,
        live: true,
      };
    return item;
  });
}

function fmtAsOf(iso: string): string {
  const d = new Date(iso);
  const mo = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const dy = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${mo} ${dy} · ${hh}:${mm} UTC`;
}

export function Header() {
  const { t } = useTranslation();
  const me = useAuth((s) => s.me);
  const signOutLocal = useAuth((s) => s.signOutLocal);
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [menu, setMenu] = useState(false);
  const [tapeStats, setTapeStats] = useState<TapeStats | null>(null);

  useEffect(() => {
    const b = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(b);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchTape() {
      try {
        const data = await apiFetch<TapeStats>('/v1/stats/tape');
        if (!cancelled) setTapeStats(data);
      } catch (err) {
        console.error('[tape] fetch failed', err);
      }
    }
    void fetchTape();
    const t = setInterval(() => void fetchTape(), 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const cls = CLASS_TONE[me?.classification ?? 'PUBLIC'] ?? CLASS_TONE.PUBLIC!;
  const initials = (me?.email ?? '??').slice(0, 2).toUpperCase();
  const activeTape = applyTapeStats(TAPE, tapeStats);

  async function signOut() {
    setMenu(false);
    try {
      await authFetch('/v1/auth/logout', { method: 'POST' });
    } catch {
      // even if logout fails (network), purge local state
    }
    signOutLocal();
    void navigate({ to: '/login' });
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div
        className={`class-stripe ${classKey(me?.classification)}`}
        style={{ background: cls.bg }}
      />

      <div className="strip shell-header-strip" style={{ height: 36, padding: '0 12px', gap: 14 }}>
        {/* product mark — monospace glyph (sun glyph moved to NavRail theme toggle) */}
        <div
          className="header-mark"
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: 168, flexShrink: 0 }}
        >
          <span
            aria-hidden
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 16,
              lineHeight: 1,
              color: 'var(--primary)',
            }}
          >
            ▣
          </span>
          <span
            style={{ fontFamily: 'var(--sans)', fontWeight: 600, letterSpacing: 1.4, fontSize: 13 }}
          >
            {t('shell.header.brand')}
          </span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-3)', fontSize: 10 }}>
            {t('shell.header.version')}
          </span>
        </div>

        <div className="vr header-vr" />

        {/* tenant chip — replaces the placeholder CASE selector until Cases ship */}
        <div
          className="header-tenant"
          style={{
            height: 24,
            padding: '0 10px',
            border: '1px solid var(--bd-strong)',
            background: 'var(--bg-pane-2)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontWeight: 500, letterSpacing: 0.4 }}>
            {t('shell.header.tenant')}
          </span>
          <span style={{ color: 'var(--fg-1)', fontFamily: 'var(--mono)' }}>
            {me?.tenant_name ?? '—'}
          </span>
        </div>

        {/* LIVE pill — fixed-width time so the pill never resizes */}
        <Pill tone="green">
          {t('shell.header.live')} ·{' '}
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontVariantNumeric: 'tabular-nums',
              display: 'inline-block',
              width: '7ch',
            }}
          >
            {timeShort(now)}
          </span>
        </Pill>

        {/* Bloomberg-style scrolling ticker tape — all items scroll left continuously */}
        <div
          className="header-tape"
          style={{
            flex: 1,
            overflow: 'hidden',
            height: 24,
            border: '1px solid var(--bd)',
            background: 'var(--bg-pane)',
            position: 'relative',
          }}
        >
          <div className="tape-track">
            {/* doubled for seamless loop */}
            {[...activeTape, ...activeTape].map((item, i) => (
              <Tooltip
                key={`${item.k}-${i < activeTape.length ? 'a' : 'b'}`}
                ariaLabel={`${item.k}: ${item.v}. ${t(item.descKey)}`}
                content={<TapeCard item={item} />}
                maxWidth={360}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    gap: 8,
                    alignItems: 'center',
                    padding: '0 20px',
                    height: 22,
                    borderRight: '1px solid var(--bd)',
                    fontSize: 11,
                    fontFamily: 'var(--mono)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: 'var(--fg-3)', letterSpacing: 0.3, fontWeight: 500 }}>
                    {item.k}
                  </span>
                  <span style={{ color: item.live ? 'var(--fg-1)' : 'var(--fg-3)' }}>{item.v}</span>
                  {item.d != null && (
                    <span
                      style={{
                        color: item.d > 0 ? 'var(--green)' : 'var(--red)',
                        fontWeight: 600,
                      }}
                    >
                      {item.d > 0 ? '▲' : '▼'}
                      {Math.abs(item.d)}
                    </span>
                  )}
                </span>
              </Tooltip>
            ))}
          </div>
        </div>

        {/* classification + user menu */}
        <div
          className="header-user-group"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 11,
            color: 'var(--fg-2)',
            position: 'relative',
          }}
        >
          <span className="header-clr-label" style={{ fontFamily: 'var(--mono)' }}>
            <span style={{ color: 'var(--fg-3)' }}>{t('shell.header.clr')} </span>
            {cls.label}
          </span>
          <LocaleToggle />
          <button
            type="button"
            className="header-user-btn"
            onClick={() => setMenu((m) => !m)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 24,
              padding: '0 6px',
              border: '1px solid var(--bd-strong)',
              background: 'var(--bg-pane-2)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
              fontSize: 11,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--primary)',
                background: 'var(--primary-bg-faint)',
                border: '1px solid var(--primary-line)',
              }}
            >
              {initials}
            </div>
            <span className="mono header-email">{me?.email ?? '—'}</span>
            <span style={{ color: 'var(--fg-3)' }}>▾</span>
          </button>
          {menu && (
            <>
              <div
                onClick={() => setMenu(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 4px)',
                  width: 240,
                  background: 'var(--bg-pane)',
                  border: '1px solid var(--bd-strong)',
                  zIndex: 31,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--bd)' }}>
                  <div className="lbl">{t('shell.header.signedInAs')}</div>
                  <div className="mono" style={{ fontSize: 12, marginTop: 2 }}>
                    {me?.email ?? '—'}
                  </div>
                  <div className="meta mono" style={{ marginTop: 1 }}>
                    {me?.role.replace('_', ' ').toUpperCase() ?? ''} · {me?.tenant_name ?? ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenu(false);
                    void navigate({ to: '/terminal' });
                  }}
                  className="btn gh"
                  style={{
                    height: 28,
                    justifyContent: 'flex-start',
                    border: 'none',
                    borderRadius: 0,
                  }}
                >
                  {t('shell.header.workspace')}
                </button>
                <button
                  type="button"
                  onClick={signOut}
                  className="btn gh"
                  style={{
                    height: 28,
                    justifyContent: 'flex-start',
                    border: 'none',
                    borderRadius: 0,
                    color: 'var(--red)',
                  }}
                >
                  {t('shell.header.signOut')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function classKey(c?: string): string {
  switch (c) {
    case 'CUI':
      return 'cui';
    case 'SECRET':
      return 'sec';
    case 'TS':
    case 'TS_SCI':
      return 'ts';
    default:
      return 'pub';
  }
}

function TapeCard({ item }: { item: TapeItem }) {
  const { t } = useTranslation();
  return (
    <div style={{ width: 340, fontFamily: 'var(--mono)' }}>
      {/* header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: 'var(--fg-1)' }}>
          {item.label}
        </span>
        <span
          style={{
            fontSize: 10,
            letterSpacing: 0.6,
            color: item.live ? 'var(--green)' : 'var(--fg-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 8, lineHeight: 1 }}>{item.live ? '●' : '○'}</span>
          {item.live ? 'LIVE' : 'NO FEED'}
        </span>
      </div>

      {/* main metric */}
      <div style={{ padding: '12px 12px 10px' }}>
        <div
          style={{
            fontSize: 32,
            fontWeight: 700,
            lineHeight: 1,
            color: item.live ? 'var(--fg-1)' : 'var(--fg-3)',
            letterSpacing: -0.5,
          }}
        >
          {item.v}
        </div>
        {item.metricLabel && (
          <div style={{ marginTop: 4, fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.3 }}>
            {item.metricLabel}
          </div>
        )}
      </div>

      {/* description */}
      <div
        style={{
          padding: '0 12px 10px',
          fontSize: 11,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
          fontFamily: 'var(--sans)',
        }}
      >
        {t(item.descKey)}
      </div>

      {/* footer */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px solid var(--bd)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          fontSize: 10,
          color: 'var(--fg-3)',
        }}
      >
        <div>
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {t('shell.header.source')}
          </span>
          {' · '}
          {item.sourceUrl ? (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--cyan)', textDecoration: 'none' }}
            >
              {item.source}
            </a>
          ) : (
            item.source
          )}
        </div>
        <div>
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {t('shell.header.asOf')}
          </span>
          {' · '}
          <span style={{ color: item.live ? 'var(--green)' : 'var(--fg-3)' }}>
            {item.asOf ? fmtAsOf(item.asOf) : item.live ? 'LIVE' : 'NO FEED'}
          </span>
        </div>
      </div>
    </div>
  );
}
